/**
 * Formprüfung eingehender Aktionen — bevor `apply` sie anfasst.
 *
 * Der Typ `Action` gilt nur zur Übersetzungszeit. Zur Laufzeit kommt eine
 * Aktion als JSON vom Server, aus einer Änderungsdatei oder aus einem Backup,
 * und dort kann alles stehen. Ein einziges beschädigtes Ereignis
 * (`match.result` ohne `result`) brachte vorher `validateResult` mit einem
 * TypeError zum Absturz — und weil jedes Gerät das Protokoll beim Abgleich neu
 * abspielt, stand der Sync auf **allen** Geräten dauerhaft still.
 *
 * Geprüft wird nur, was billig ist und was `apply` ungeprüft benutzt: Pflicht-
 * felder und ihre Grundtypen. Inhaltliche Regeln bleiben bei `apply`.
 */

type Art = 'id' | 'string' | 'number' | 'object' | 'array' | 'ab';
type Feld = readonly [name: string, art: Art, optional?: true];

const pflicht = (name: string, art: Art): Feld => [name, art];
const kann = (name: string, art: Art): Feld => [name, art, true];

/** Pflicht- und Kann-Felder je Aktionsart; unbekannte Arten lässt `apply` ohnehin liegen */
const FELDER: Record<string, readonly Feld[]> = {
  'tournament.update': [pflicht('patch', 'object')],
  'tournament.setStatus': [pflicht('status', 'string')],
  'tournament.pause': [kann('until', 'number')],
  revert: [pflicht('targetId', 'id')],
  'court.add': [pflicht('name', 'string'), kann('group', 'string'), kann('priority', 'number')],
  'court.update': [pflicht('courtId', 'id'), pflicht('patch', 'object')],
  'court.remove': [pflicht('courtId', 'id')],
  'competition.add': [pflicht('name', 'string'), pflicht('ruleSet', 'object')],
  'competition.updateRules': [pflicht('competitionId', 'id'), pflicht('patch', 'object')],
  'team.add': [pflicht('competitionId', 'id'), pflicht('team', 'object'), kann('groupId', 'id')],
  'team.update': [pflicht('competitionId', 'id'), pflicht('teamId', 'id'), pflicht('patch', 'object')],
  'team.withdraw': [pflicht('competitionId', 'id'), pflicht('teamId', 'id')],
  'team.remove': [pflicht('competitionId', 'id'), pflicht('teamId', 'id')],
  'team.setPlayers': [pflicht('competitionId', 'id'), pflicht('teamId', 'id'), pflicht('players', 'array')],
  'team.clearAll': [pflicht('competitionId', 'id')],
  'team.clubsFromNames': [pflicht('competitionId', 'id')],
  'phase.add': [pflicht('competitionId', 'id'), pflicht('config', 'object'), kann('ruleSetOverride', 'object')],
  'phase.updateConfig': [pflicht('phaseId', 'id'), pflicht('config', 'object')],
  'phase.remove': [pflicht('phaseId', 'id')],
  'phase.setOptions': [pflicht('phaseId', 'id'), pflicht('patch', 'object')],
  'phase.draw': [pflicht('phaseId', 'id'), kann('seed', 'string')],
  'phase.moveTeam': [pflicht('phaseId', 'id'), pflicht('teamId', 'id'), pflicht('toGroupId', 'id')],
  'phase.freeze': [pflicht('phaseId', 'id')],
  'phase.finish': [pflicht('phaseId', 'id')],
  'phase.setCourtGroup': [pflicht('phaseId', 'id'), kann('roundId', 'id'), kann('courtGroup', 'string')],
  'ko.startRound': [pflicht('phaseId', 'id'), pflicht('teamIds', 'array'), kann('label', 'string'), kann('courtGroup', 'string')],
  'group.resolveLot': [pflicht('phaseId', 'id'), pflicht('groupId', 'id'), pflicht('order', 'array')],
  'group.setCourtGroup': [pflicht('phaseId', 'id'), pflicht('groupId', 'id'), kann('courtGroup', 'string')],
  'match.start': [pflicht('matchId', 'id'), pflicht('courtId', 'id')],
  'match.score': [pflicht('matchId', 'id')],
  'match.result': [pflicht('matchId', 'id'), pflicht('result', 'object')],
  'match.walkover': [pflicht('matchId', 'id'), pflicht('winner', 'ab')],
  'match.reopen': [pflicht('matchId', 'id')],
  'match.abort': [pflicht('matchId', 'id')],
  'match.setPriority': [pflicht('matchId', 'id'), pflicht('priority', 'number')],
  'match.order': [pflicht('matchIds', 'array')],
  'match.cancel': [pflicht('matchId', 'id')],
};

function passt(v: unknown, art: Art): boolean {
  switch (art) {
    case 'id':
      return typeof v === 'string' && v.length > 0;
    case 'string':
      return typeof v === 'string';
    case 'number':
      return typeof v === 'number' && Number.isFinite(v);
    case 'object':
      return !!v && typeof v === 'object' && !Array.isArray(v);
    case 'array':
      return Array.isArray(v);
    case 'ab':
      return v === 'A' || v === 'B';
  }
}

const istObjekt = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/** Liefert `null`, wenn die Aktion die nötige Form hat, sonst den Grund */
export function formFehler(action: unknown): string | null {
  if (!istObjekt(action)) return 'Aktion beschädigt: kein Objekt';
  const type = action.type;
  if (typeof type !== 'string') return 'Aktion beschädigt: Art fehlt';
  const felder = FELDER[type];
  if (!felder) return null;
  for (const [name, art, optional] of felder) {
    const v = action[name];
    if (v === undefined && optional) continue;
    if (!passt(v, art)) return `Aktion ${type} beschädigt: Feld ${name} fehlt oder passt nicht`;
  }
  // Tiefer nur dort, wo `apply` ohne weitere Prüfung hineingreift
  if (type === 'team.add') {
    const team = action.team as Record<string, unknown>;
    if (!Array.isArray(team.players) || !team.players.every(istObjekt)) return `Aktion ${type} beschädigt: Feld team fehlt oder passt nicht`;
  }
  if (type === 'match.result') {
    const r = action.result as Record<string, unknown>;
    const setsOk =
      Array.isArray(r.sets) && r.sets.every((s) => istObjekt(s) && (s.innings === undefined || (Array.isArray(s.innings) && s.innings.every((i) => istObjekt(i) && istObjekt(i.a) && istObjekt(i.b)))));
    const winnerOk = r.winner === 'A' || r.winner === 'B' || r.winner === null;
    if (!setsOk || typeof r.endedBy !== 'string' || !winnerOk) return `Aktion ${type} beschädigt: Feld result fehlt oder passt nicht`;
  }
  if (type === 'phase.add' || type === 'phase.updateConfig') {
    if (typeof (action.config as Record<string, unknown>).type !== 'string') return `Aktion ${type} beschädigt: Feld config fehlt oder passt nicht`;
  }
  return null;
}
